import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Gallery = () => {
  const { t, language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxPosition, setLightboxPosition] = useState(0);
  
  // Dil bazlı rezervasyon path
  const reservationsPath = language === 'de' ? '/reservierungen' : '/reservations';

  const categories = [
    { id: 'all', label: t.gallery.categories.all },
    { id: 'food-truck', label: t.gallery.categories.foodTruck },
    { id: 'food', label: t.gallery.categories.food },
    { id: 'drinks', label: t.gallery.categories.drinks },
    { id: 'events', label: t.gallery.categories.events }
  ];

  // Premium SLÁI Events branded images - local optimized files
  const galleryImages = [
    { id: 0, category: 'events', src: './images/gallery-0.jpg' }, // Christmas Market Strawberry
    { id: 1, category: 'events', src: './images/gallery-1.png' }, // Cocktail Party Berlin
    { id: 2, category: 'events', src: './images/gallery-2.png' }, // Firma Event Berlin
    { id: 3, category: 'events', src: './images/gallery-3.png' }, // Flohmarkt Berlin
    { id: 4, category: 'events', src: './images/gallery-4.png' }, // Flohmarkt Event Berlin
    { id: 5, category: 'events', src: './images/gallery-5.png' }, // Halloween Party Berlin
    { id: 6, category: 'events', src: './images/gallery-6.png' }, // Hochzeit Event Berlin
    { id: 7, category: 'events', src: './images/gallery-7.png' }, // Kinder Eis Event Berlin
    { id: 8, category: 'events', src: './images/gallery-8.png' }  // Konzert Event Berlin
  ];

  const filteredImages = selectedCategory === 'all'
    ? galleryImages
    : galleryImages.filter(img => img.category === selectedCategory);

  // Open lightbox with selected image (rezervasyon sayfası gibi)
  const openLightbox = (imageId) => {
    const index = filteredImages.findIndex(img => img.id === imageId);
    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    setLightboxPosition(scrollY + viewportHeight / 2);
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? filteredImages.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === filteredImages.length - 1 ? 0 : prev + 1));
  };

  // Arka scroll'u kilitle - basitleştirilmiş versiyon
  useEffect(() => {
    if (lightboxOpen) {
      // Body'yi kilitle - scroll pozisyonunu değiştirme
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.documentElement.style.overflow = 'hidden';
    } else {
      // Sadece kilidi kaldır
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
    };
  }, [lightboxOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, filteredImages.length]);

  return (
    <div className="min-h-screen pt-16 bg-[#1a1a1a]">
      {/* Header */}
      <section className="py-12 sm:py-20 bg-[#1a1a1a] border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'de' ? 'UNSERE ARBEITEN' : 'OUR WORK'}
            </p>
            <h1 className="text-4xl sm:text-6xl md:text-7xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
              {t.gallery.title}
            </h1>
            <div className="w-16 h-[1px] bg-white/40 mx-auto mb-6" />
            <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {t.gallery.subtitle}
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category.id)}
                size="sm"
                className={`transition-all duration-300 border text-xs tracking-[0.15em] uppercase rounded-none ${
                  selectedCategory === category.id
                    ? 'bg-[#3a3a3a] text-white border-[#e67e22]'
                    : 'bg-transparent text-white/70 border-white/30 hover:border-[#e67e22] hover:bg-[#3a3a3a] hover:text-white'
                }`}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 sm:py-16 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredImages.map((image) => (
              <Card
                key={image.id}
                className="overflow-hidden group cursor-pointer rounded-xl border border-white/10 bg-[#252525] shadow-none hover:border-[#e67e22] transition-all duration-500"
                onClick={() => openLightbox(image.id)}
              >
                <div className="aspect-square relative overflow-hidden bg-[#1a1a1a]">
                  <img
                    src={image.src}
                    alt=""
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredImages.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-white/60" style={{ fontFamily: "'Montserrat', sans-serif" }}>No images in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {language === 'de' ? 'BEREIT ZU BEGINNEN?' : 'READY TO START?'}
          </p>
          <h2 className="text-4xl sm:text-5xl text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
            {t.gallery.ctaTitle}
          </h2>
          <div className="w-16 h-[1px] bg-white/40 mx-auto mb-8" />
          <p className="text-base sm:text-lg text-white/70 mb-10 max-w-2xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
            {t.gallery.ctaSubtitle}
          </p>
          <Link to={reservationsPath}>
            <Button
              className="bg-[#3a3a3a] text-white border border-[#e67e22] hover:bg-[#4a4a4a] text-xs tracking-[0.2em] uppercase px-12 py-6 rounded-none"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {t.gallery.ctaButton}
            </Button>
          </Link>
        </div>
      </section>

      {/* Lightbox Modal - Rezervasyon sayfası gibi aktif görüntü alanında */}
      {lightboxOpen && (
        <>
          {/* Overlay - fixed, header dahil her şeyi kaplar */}
          <div 
            className="fixed inset-0 bg-black/90 z-[9999]"
            onClick={closeLightbox}
          />
          
          {/* Lightbox - absolute, aktif görüntü alanının ortasında */}
          <div 
            className="absolute left-1/2 z-[10000]"
            style={{ 
              transform: 'translate(-50%, -50%)',
              top: `${lightboxPosition}px`
            }}
          >
            {/* Image Container */}
            <div className="relative">
              <img
                src={filteredImages[currentImageIndex]?.src}
                alt=""
                className="max-w-[75vw] max-h-[70vh] object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Counter - resmin içinde, altta ortada */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/5/70 px-4 py-2 rounded-full">
                <span className="text-white text-sm font-medium">
                  {currentImageIndex + 1} / {filteredImages.length}
                </span>
              </div>
            </div>
          </div>

          {/* Left Arrow - ekranın sol kenarında, lightbox ile aynı dikey hizada */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 z-[10001] p-3 bg-gray-700/80 hover:bg-gray-600 rounded-full transition-colors"
            style={{ 
              top: `${lightboxPosition}px`,
              transform: 'translateY(-50%)'
            }}
            aria-label="Previous"
          >
            <ChevronLeft className="w-8 h-8 text-white" />
          </button>

          {/* Right Arrow - ekranın sağ kenarında, lightbox ile aynı dikey hizada */}
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 z-[10001] p-3 bg-gray-700/80 hover:bg-gray-600 rounded-full transition-colors"
            style={{ 
              top: `${lightboxPosition}px`,
              transform: 'translateY(-50%)'
            }}
            aria-label="Next"
          >
            <ChevronRight className="w-8 h-8 text-white" />
          </button>

          {/* X Button - absolute, overlay üzerinde sabit pozisyon */}
          <div 
            className="absolute z-[10001]"
            style={{ 
              top: `${lightboxPosition - 350}px`,
              right: 24
            }}
          >
            <button
              onClick={closeLightbox}
              className="p-2 bg-gray-700/80 hover:bg-gray-600 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-8 h-8 text-white" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Gallery;
